import React, {useState} from 'react';
import {Container, Tab, LoginButton} from '../../styles/Styles';
import AdminNav from './AdminNav';
import {useAuth} from '../../context/AuthContext';
import {Roles} from '../common/roles';
import {Navigate} from 'react-router-dom';
import UploadTourListForm from "../form/UploadTourListForm";
import {ReservationSystem} from "../common/bookingSystel";
import {useQuery, useMutation} from "@apollo/client";
import {GET_BOOKINGS_BY_SYSTEM, COMBINE_BOOKINGS_INTO_TOUR} from "../gql/bookings-gql";
import {GET_TOURS_LABEL_LIST} from "../gql/tours-gql";
import {GET_GUIDES_LIST} from "../gql/guides-gql";
import moment from "moment/moment";
import {Calendar, momentLocalizer} from 'react-big-calendar';
import "react-big-calendar/lib/css/react-big-calendar.css";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import AddBookingForm from "../form/AddBookingForm";
import Loader from "../common/Loader";
import AppModal from "../common/AppModal";
import styled from "styled-components";
import {toast} from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {Booking} from "../common/interfaces";
import {Languages} from "../common/languages";

const DnDCalendar = withDragAndDrop(Calendar);
const localizer = momentLocalizer(moment);

const BookingCard = styled.div`
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 1.5rem;
    margin-bottom: 1rem;
    transition: transform 0.2s, box-shadow 0.2s;
    cursor: pointer;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }

    &.selected {
        background-color: #e3f2fd;
        border: 2px solid #2196f3;
    }
`;

const BookingHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #eee;
`;

const BookingTitle = styled.h3`
    margin: 0;
    color: #333;
    font-size: 1.2rem;
`;

const BookingStatus = styled.span<{ status: string }>`
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.9rem;
    font-weight: 500;
    background-color: ${props => props.status === 'ACTIVE' ? '#e8f5e9' : '#ffebee'};
    color: ${props => props.status === 'ACTIVE' ? '#2e7d32' : '#c62828'};
`;

const BookingDetails = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
`;

const DetailLabel = styled.span`
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 0.25rem;
`;

const DetailValue = styled.span`
    font-weight: 500;
    color: #333;
`;

const ClientInfo = styled.div`
    background: #f5f5f5;
    padding: 1rem;
    border-radius: 6px;
    margin-top: 1rem;
`;

const ClientTitle = styled.h4`
    margin: 0 0 0.5rem 0;
    color: #333;
    font-size: 1rem;
`;

const ClientDetails = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.5rem;
`;

const CalendarContainer = styled.div`
    height: 600px;
    margin: 2rem 0;
    background: white;
    padding: 1rem;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const BookingListContainer = styled.div`
    margin-top: 2rem;
`;

const TabContainer = styled.div`
    margin: 2rem 0;
`;

const CombineBookingsModal = styled.div`
    padding: 2rem;
    max-width: 600px;
    margin: 0 auto;
`;

const FormGroup = styled.div`
    margin-bottom: 1.5rem;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
`;

const Select = styled.select`
    width: 100%;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    background-color: white;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #4CAF50;
    }
`;

const BookingsList = styled.div`
    margin: 1rem 0;
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 1rem;
`;

const BookingItem = styled.div`
    padding: 0.5rem;
    border-bottom: 1px solid #eee;
    
    &:last-child {
        border-bottom: none;
    }
`;

const DateSeparator = styled.div`
    display: flex;
    align-items: center;
    margin: 1.5rem 0 0.75rem;
    color: #666;
    font-size: 0.95rem;
    font-weight: 600;

    &::before,
    &::after {
        content: "";
        flex: 1;
        height: 1px;
        background: #e0e0e0;
    }

    &::before {
        margin-right: 0.75rem;
    }

    &::after {
        margin-left: 0.75rem;
    }
`;


const AdminDashboardBookings = () => {
    const { user, isAuthenticated } = useAuth();
    const [activeTab, setActiveTab] = useState(0);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedEvent, setSelectedEvent] = useState<Booking | null>(null);
    const [showEventModal, setShowEventModal] = useState(false);
    const [selectedBookings, setSelectedBookings] = useState<string[]>([]);
    const [showCombineModal, setShowCombineModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [selectedGuide, setSelectedGuide] = useState('');
    const [selectedTourLabel, setSelectedTourLabel] = useState('');
    const [selectedLanguage, setSelectedLanguage] = useState('');

    const LanguagesArray = Object.values(Languages);

    //const { data: tourItems } = useQuery(GET_TOUR_ITEMS_LIST);
    const { data: tourLabels } = useQuery(GET_TOURS_LABEL_LIST);
    const { data: guidesList } = useQuery(GET_GUIDES_LIST);

    const [combineBookings] = useMutation(COMBINE_BOOKINGS_INTO_TOUR, {
        onCompleted: () => {
            toast.success("Bookings combined successfully!");
            setSelectedBookings([]);
            setSelectedLanguage('');
            setShowCombineModal(false);
            refetch();
        },
        onError: (error) => {
            toast.error("Failed to combine bookings: " + error.message);
        }
    });

    const handleCombineBookings = async () => {
        if (!selectedTourLabel || !selectedDate) {
            toast.warning("Please select a tour label and date");
            return;
        }

        try {
            await combineBookings({
                variables: {
                    tourCreateInput: {
                        bookingsToCombine: selectedBookings,
                        tourLabel: selectedTourLabel,
                        tourDate: selectedDate,
                        guide: selectedGuide || undefined,
                        language: selectedLanguage || undefined,
                    }
                }
            });
        } catch (error) {
            console.error("Error combining bookings:", error);
        }
    };

    const getRS = () => {
        switch(activeTab) {
            case (0 || 3) : return ReservationSystem.ALL;
            case 1 : return ReservationSystem.GYG;
            case 2 : return ReservationSystem.VIATOR;
            default:
                console.warn("Unexpected activeTab value:", activeTab);
                return ReservationSystem.ALL; // Fallback
        }
    }

    const {data: bookings, loading, refetch } = useQuery(GET_BOOKINGS_BY_SYSTEM, {
        variables: {
            bookingInput: {
                reservationSystem: getRS(),
                month: currentMonth.getMonth() + 1,
                year: currentMonth.getFullYear()
            }
        }
    });

    const onTabSelected = (index: any) => {
        setActiveTab(index);
    };

    const handleCalendarNavigate = (date: Date) => {
        setCurrentMonth(date);
    };

    const bookingList = bookings?.getBookingsBySystem?.filter((el: any) => !el.tour) || null;

    const events = bookingList?.map((booking: any, index: number) => ({
        id: booking._id,
        calendarId: '1',
        title: `${booking?.tourItem?.title}`,
        body: booking?.client?.clientName,
        isAllday: "false",
        category: 'time',
        start: moment(booking.bookingDate).toDate(),
        end: moment(booking.bookingDate).add(1, 'hour').toDate(),
        isReadOnly: true,
        bgColor: booking.status === 'ACTIVE' ? '#00a9ff' : '#ff4040',
        resource: booking // Store the full booking data
    }));

    const onEventResize = () => {};

    const onEventDrop = (data: any) => {
        console.log(data);
    };

    const handleSelectEvent = (event: any) => {
        setSelectedEvent(event.resource);
        setShowEventModal(true);
    };

    const handleBookingSelect = (bookingId: string) => {
        setSelectedBookings(prev => {
            if (prev.includes(bookingId)) {
                return prev.filter(id => id !== bookingId);
            } else {
                return [...prev, bookingId];
            }
        });
    };

    const renderBookingCard = (booking: any) => (
        <BookingCard
            key={booking._id}
            onClick={() => handleBookingSelect(booking._id)}
            className={selectedBookings.includes(booking._id) ? 'selected' : ''}
        >
            <BookingHeader>
                <BookingTitle>{booking?.tourItem?.title}</BookingTitle>
                <BookingStatus status={booking.status}>{booking.status}</BookingStatus>
            </BookingHeader>

            <BookingDetails>
                <DetailItem>
                    <DetailLabel>Booking Code</DetailLabel>
                    <DetailValue>{booking.bookingCode}</DetailValue>
                </DetailItem>
                <DetailItem>
                    <DetailLabel>Date</DetailLabel>
                    <DetailValue>{moment(booking.bookingDate).format("D MMM YYYY h:mm a")}</DetailValue>
                </DetailItem>
                <DetailItem>
                    <DetailLabel>Client Price</DetailLabel>
                    <DetailValue>€{booking.price}</DetailValue>
                </DetailItem>
                <DetailItem>
                    <DetailLabel>Our Price</DetailLabel>
                    <DetailValue>€{booking.priceNet}</DetailValue>
                </DetailItem>
            </BookingDetails>

            <ClientInfo>
                <ClientTitle>Client Information</ClientTitle>
                <ClientDetails>
                    <DetailItem>
                        <DetailLabel>Name</DetailLabel>
                        <DetailValue>{booking.client.clientName}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Phone</DetailLabel>
                        <DetailValue>{booking.client.clientPhone}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Adults</DetailLabel>
                        <DetailValue>{booking.client.clientAdult}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Youths</DetailLabel>
                        <DetailValue>{booking.client.clientYouth}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Children</DetailLabel>
                        <DetailValue>{booking.client.clientChild}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Enfants</DetailLabel>
                        <DetailValue>{booking.client.clientEnfant}</DetailValue>
                    </DetailItem>
                </ClientDetails>
            </ClientInfo>
        </BookingCard>
    );

    const renderBookingList = (list: any[] | null) => {
        if (!list?.length) return null;
        const sorted = [...list].sort(
            (a, b) => new Date(a.bookingDate).getTime() - new Date(b.bookingDate).getTime()
        );
        let lastDateKey = "";
        return sorted.map((booking) => {
            const dateKey = moment(booking.bookingDate).format("YYYY-MM-DD");
            const showSeparator = dateKey !== lastDateKey;
            lastDateKey = dateKey;
            return (
                <React.Fragment key={booking._id}>
                    {showSeparator && (
                        <DateSeparator>
                            {moment(booking.bookingDate).format("MMMM D, YYYY")}
                        </DateSeparator>
                    )}
                    {renderBookingCard(booking)}
                </React.Fragment>
            );
        });
    };

    if (!isAuthenticated || user?.role[0] !== Roles.ADMIN) {
        return <Navigate to="/login" />;
    }

    if (loading) return <Loader />;

    return (
        <Container>
            <AdminNav />
            <h2>Bookings Management</h2>

            <TabContainer>
                <Tab>
                    <div className={activeTab === 0 ? "selected" : ""} onClick={() => onTabSelected(0)}>All</div>
                    <div className={activeTab === 1 ? "selected" : ""} onClick={() => onTabSelected(1)}>GetYourGuide</div>
                    <div className={activeTab === 2 ? "selected" : ""} onClick={() => onTabSelected(2)}>Viator</div>
                    <div className={activeTab === 3 ? "selected" : ""} onClick={() => onTabSelected(3)}>Manually</div>
                </Tab>
            </TabContainer>

            {activeTab === 0 && (
                <>
                    <CalendarContainer>
                        <DnDCalendar
                            date={currentMonth}
                            defaultView="month"
                            events={events}
                            localizer={localizer}
                            onEventDrop={onEventDrop}
                            onEventResize={onEventResize}
                            onSelectEvent={handleSelectEvent}
                            onNavigate={handleCalendarNavigate}
                            resizable
                            style={{ height: "100%" }}
                        />
                    </CalendarContainer>
                    {selectedBookings.length > 0 && (
                        <LoginButton
                            onClick={() => setShowCombineModal(true)}
                            style={{ margin: '20px 0' }}
                        >
                            Create tour ({selectedBookings.length})
                        </LoginButton>
                    )}
                    <BookingListContainer>
                        {renderBookingList(bookingList)}
                    </BookingListContainer>
                </>
            )}

            {activeTab === 1 && (
                <>
                    <UploadTourListForm bookingLabel={ReservationSystem.GYG} />
                    <CalendarContainer>
                        <DnDCalendar
                            date={currentMonth}
                            defaultView="month"
                            events={events}
                            localizer={localizer}
                            onEventDrop={onEventDrop}
                            onEventResize={onEventResize}
                            onSelectEvent={handleSelectEvent}
                            onNavigate={handleCalendarNavigate}
                            resizable
                            style={{ height: "100%" }}
                        />
                    </CalendarContainer>
                    <BookingListContainer>
                        {renderBookingList(bookingList)}
                    </BookingListContainer>
                </>
            )}

            {activeTab === 2 && (
                <>
                    <UploadTourListForm bookingLabel={ReservationSystem.VIATOR} />
                    <BookingListContainer>
                        {renderBookingList(bookingList)}
                    </BookingListContainer>
                </>
            )}

            {activeTab === 3 && (
                <AddBookingForm onClose={() => {
                    setActiveTab(0);
                    refetch();
                }} />
            )}

            <AppModal
                isOpen={showEventModal}
                onClose={() => setShowEventModal(false)}
                className={null}
            >
                {selectedEvent && renderBookingCard(selectedEvent)}
            </AppModal>

            <AppModal
                isOpen={showCombineModal}
                onClose={() => {
                    setShowCombineModal(false);
                    setSelectedLanguage('');
                }}
                className={null}
            >
                <CombineBookingsModal>
                    <h2>Combine Bookings</h2>

                    <FormGroup>
                        <Label>Tour Label</Label>
                        <Select
                            value={selectedTourLabel}
                            onChange={(e) => setSelectedTourLabel(e.target.value)}
                        >
                            <option value="">Select a tour label</option>
                            {tourLabels?.getTourLabelsList?.map((label: any) => (
                                <option key={label._id} value={label._id}>
                                    {label.title.title}
                                </option>
                            ))}
                        </Select>
                    </FormGroup>

                    <FormGroup>
                        <Label>Tour Date and Time</Label>
                        <DatePicker
                            selected={selectedDate}
                            onChange={(date: Date | null) => date && setSelectedDate(date)}
                            showTimeSelect
                            dateFormat="MMMM d, yyyy h:mm aa"
                            className="date-picker"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>Select Guide (Optional)</Label>
                        <Select
                            value={selectedGuide}
                            onChange={(e) => setSelectedGuide(e.target.value)}
                        >
                            <option value="">No guide selected</option>
                            {guidesList?.getGuidesList?.map((guide: any) => (
                                <option key={guide._id} value={guide._id}>
                                    {guide.profile.firstName} {guide.profile.lastName}
                                </option>
                            ))}
                        </Select>
                    </FormGroup>

                    <FormGroup>
                        <Label>Language (Optional)</Label>
                        <Select
                            value={selectedLanguage}
                            onChange={(e) => setSelectedLanguage(e.target.value)}
                        >
                            <option value="">No language selected</option>
                            {LanguagesArray.map((lang) => (
                                <option key={lang} value={lang}>
                                    {lang}
                                </option>
                            ))}
                        </Select>
                    </FormGroup>

                    <FormGroup>
                        <Label>Selected Bookings</Label>
                        <BookingsList>
                            {bookingList?.filter((booking: any) => selectedBookings.includes(booking._id))
                                .map((booking: any) => (
                                    <BookingItem key={booking._id}>
                                        <div>ID: {booking._id}</div>
                                        <div>Date: {moment(booking.bookingDate).format('MMMM D, YYYY h:mm A')}</div>
                                        <div>Title: {booking?.tourItem?.title}</div>
                                        <div>Travelers: {
                                            (booking.client?.clientAdult || 0) +
                                            (booking.client?.clientChild || 0) +
                                            (booking.client?.clientYouth || 0) +
                                            (booking.client?.clientEnfant || 0)
                                        }</div>
                                    </BookingItem>
                                ))}
                        </BookingsList>
                    </FormGroup>

                    <LoginButton onClick={handleCombineBookings}>
                        Combine Bookings
                    </LoginButton>
                </CombineBookingsModal>
            </AppModal>
        </Container>
    );
};

export default AdminDashboardBookings;
